import { fetchConfig } from '@theme/utilities';
import { CartUpdateEvent } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * Lets shoppers replace a cart line with another variant of the same product
 * without leaving the cart drawer.
 */
class CartVariantSelect extends HTMLElement {
  #select;
  #error;
  #onChangeBound;

  connectedCallback() {
    this.#select = this.querySelector('select');
    this.#error = this.querySelector('.cart-items__variant-select-error');

    if (!(this.#select instanceof HTMLSelectElement)) return;

    this.#onChangeBound = this.#onChange.bind(this);
    this.#select.addEventListener('change', this.#onChangeBound);
  }

  disconnectedCallback() {
    this.#select?.removeEventListener('change', this.#onChangeBound);
  }

  async #onChange() {
    if (!(this.#select instanceof HTMLSelectElement)) return;

    const currentVariantId = Number(this.dataset.currentVariantId || 0);
    const newVariantId = Number(this.#select.value || 0);

    if (!currentVariantId || !newVariantId || currentVariantId === newVariantId) return;

    const lineKey = this.dataset.lineKey || '';
    const quantity = Math.max(1, Number(this.dataset.quantity || 1));
    const sellingPlan = this.dataset.sellingPlan ? Number(this.dataset.sellingPlan) : null;
    const properties = this.#parseProperties();
    const cartItemsComponent = this.closest('cart-items-component');
    const sourceSectionId = cartItemsComponent?.dataset.sectionId || '';
    const sectionIds = this.#getSectionIds();

    if (!lineKey || !sourceSectionId) {
      this.#restoreSelection(currentVariantId);
      return;
    }

    this.#setBusy(true);
    this.#clearError();

    let beforeCart;

    try {
      // Snapshot first so a failed add can be rolled back without touching the
      // original line item.
      beforeCart = await this.#getCart();

      const replacementItem = {
        id: newVariantId,
        quantity,
        ...(Object.keys(properties).length > 0 ? { properties } : {}),
        ...(sellingPlan ? { selling_plan: sellingPlan } : {}),
      };

      const addResponse = await this.#postJson(Theme.routes.cart_add_url, {
        items: [replacementItem],
      });

      if (!addResponse.ok || addResponse.data?.status) {
        await this.#rollbackAddedVariant(beforeCart, newVariantId, properties, sellingPlan);
        throw new Error(
          addResponse.data?.description || addResponse.data?.message || 'Unable to update this variant.'
        );
      }

      // Only remove the original line after the replacement was added.
      const removeResponse = await this.#postJson(Theme.routes.cart_change_url, {
        id: lineKey,
        quantity: 0,
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      });

      if (!removeResponse.ok || removeResponse.data?.errors || removeResponse.data?.status) {
        await this.#rollbackAddedVariant(beforeCart, newVariantId, properties, sellingPlan);
        throw new Error(
          removeResponse.data?.description ||
            removeResponse.data?.message ||
            removeResponse.data?.errors ||
            'Unable to update this variant.'
        );
      }

      const cartData = removeResponse.data || {};

      this.dispatchEvent(
        new CartUpdateEvent(cartData, sourceSectionId, {
          itemCount: Number(cartData.item_count ?? beforeCart.item_count ?? 0),
          source: 'cart-variant-selector',
          variantId: String(newVariantId),
          sections: cartData.sections || {},
        })
      );
    } catch (error) {
      console.error('[Cart variant selector]', error);
      this.#restoreSelection(currentVariantId);
      this.#showError(error instanceof Error ? error.message : String(error));
    } finally {
      this.#setBusy(false);
    }
  }

  #parseProperties() {
    try {
      const parsed = JSON.parse(this.dataset.properties || '{}');

      // Shopify can serialize an empty line-item properties collection as []
      // in Liquid. The Ajax Cart API only accepts `properties` as a hash.
      // Normalize both possible Liquid shapes and never send an Array.
      if (Array.isArray(parsed)) {
        return parsed.reduce((result, entry) => {
          if (Array.isArray(entry) && entry.length >= 2 && entry[0] != null) {
            result[String(entry[0])] = entry[1];
          } else if (entry && typeof entry === 'object') {
            const key = entry.first ?? entry.key ?? entry.name;
            const value = entry.last ?? entry.value;
            if (key != null) result[String(key)] = value;
          }
          return result;
        }, {});
      }

      if (parsed && typeof parsed === 'object') {
        return { ...parsed };
      }

      return {};
    } catch (_) {
      return {};
    }
  }

  #getSectionIds() {
    return [
      ...new Set(
        [...document.querySelectorAll('cart-items-component')]
          .map((component) => component instanceof HTMLElement ? component.dataset.sectionId : '')
          .filter(Boolean)
      ),
    ].slice(0, 5);
  }

  async #getCart() {
    const response = await fetch(`${Theme.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) throw new Error('Unable to read the cart.');
    return response.json();
  }

  async #postJson(url, payload) {
    const response = await fetch(url, fetchConfig('json', { body: JSON.stringify(payload) }));
    let data = {};

    try {
      data = await response.json();
    } catch (_) {
      data = {};
    }

    return { ok: response.ok, data };
  }

  #propertiesKey(properties) {
    const object = properties && typeof properties === 'object' ? properties : {};
    const sorted = Object.keys(object)
      .sort()
      .reduce((result, key) => {
        result[key] = object[key];
        return result;
      }, {});

    return JSON.stringify(sorted);
  }

  #lineMatches(line, variantId, properties, sellingPlan) {
    const lineSellingPlan = line?.selling_plan_allocation?.selling_plan?.id ?? null;

    return (
      Number(line?.variant_id) === Number(variantId) &&
      this.#propertiesKey(line?.properties || {}) === this.#propertiesKey(properties || {}) &&
      String(lineSellingPlan ?? '') === String(sellingPlan ?? '')
    );
  }

  async #rollbackAddedVariant(beforeCart, variantId, properties, sellingPlan) {
    const afterCart = await this.#getCart();
    const beforeQuantity = (beforeCart.items || [])
      .filter((line) => this.#lineMatches(line, variantId, properties, sellingPlan))
      .reduce((sum, line) => sum + Number(line.quantity || 0), 0);

    const matchingAfterLines = (afterCart.items || []).filter((line) =>
      this.#lineMatches(line, variantId, properties, sellingPlan)
    );
    const afterQuantity = matchingAfterLines.reduce((sum, line) => sum + Number(line.quantity || 0), 0);
    let quantityToRemove = Math.max(0, afterQuantity - beforeQuantity);

    if (quantityToRemove === 0) return;

    const updates = {};

    for (let index = matchingAfterLines.length - 1; index >= 0 && quantityToRemove > 0; index -= 1) {
      const line = matchingAfterLines[index];
      const currentQuantity = Number(line.quantity || 0);
      const reduction = Math.min(quantityToRemove, currentQuantity);
      updates[line.key] = currentQuantity - reduction;
      quantityToRemove -= reduction;
    }

    if (Object.keys(updates).length === 0) return;

    const rollbackResponse = await this.#postJson(Theme.routes.cart_update_url, { updates });
    if (!rollbackResponse.ok || rollbackResponse.data?.errors || rollbackResponse.data?.status) {
      // If rollback itself fails, refresh the section so the drawer reflects the
      // actual server state instead of displaying stale cart data.
      const sectionId = this.closest('cart-items-component')?.dataset.sectionId;
      if (sectionId) await sectionRenderer.renderSection(sectionId, { cache: false });
      throw new Error('Unable to restore the cart after a variant update error.');
    }
  }

  #restoreSelection(variantId) {
    if (this.#select instanceof HTMLSelectElement) {
      this.#select.value = String(variantId);
    }
  }

  #setBusy(isBusy) {
    if (!(this.#select instanceof HTMLSelectElement)) return;

    this.#select.disabled = isBusy;
    this.toggleAttribute('aria-busy', isBusy);
  }

  #clearError() {
    if (!(this.#error instanceof HTMLElement)) return;
    this.#error.textContent = '';
    this.#error.classList.add('hidden');
  }

  #showError(message) {
    if (!(this.#error instanceof HTMLElement) || !message) return;
    this.#error.textContent = message;
    this.#error.classList.remove('hidden');
  }
}

if (!customElements.get('cart-variant-select')) {
  customElements.define('cart-variant-select', CartVariantSelect);
}
